import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Bell, Eye, GripVertical, Pencil, Upload, Download, ChevronUp, ChevronDown, Cog, Trash2 } from "lucide-react";
import { StatusPill, Switch, Button } from "@/components/ui";
import { HOME_BLOCK_TYPE_LABELS, type HomeBlock } from "@/lib/api";
import { homeBlockPrimaryStatusMeta } from "@/lib/utils/statusMeta";
import { formatDateTime } from "@/lib/utils/format";
import { isSystemBlock } from "@/lib/utils/homeBlockGroups";
import { cn } from "@/lib/utils/cn";

export interface HomeBlockRowProps {
  block: HomeBlock;
  position: number;
  total: number;
  onEdit: () => void;
  onPreview: () => void;
  onToggleEnabled: (enabled: boolean) => void;
  onTogglePublish: () => void;
  onCreateNotification: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  selectionDisabled?: boolean;
  reorderDisabled?: boolean;
  busy: boolean;
  reordering: boolean;
}

export function HomeBlockRow({
  block,
  position,
  total,
  onEdit,
  onPreview,
  onToggleEnabled,
  onTogglePublish,
  onCreateNotification,
  onDelete,
  onMoveUp,
  onMoveDown,
  selected = false,
  onSelect,
  selectionDisabled = false,
  reorderDisabled = false,
  busy,
  reordering,
}: HomeBlockRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });
  const isSystem = isSystemBlock(block);
  const statusMeta = homeBlockPrimaryStatusMeta(block.status, block.enabled, block.effectiveStatus);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex flex-col gap-3 rounded-xl border bg-white p-3.5 shadow-card sm:flex-row sm:items-center",
        isSystem ? "border-brand-200" : "border-ink/10",
        isDragging && "opacity-60",
      )}
    >
      <div className="flex shrink-0 items-center gap-1 sm:flex-col">
        {!isSystem && onSelect ? (
          <input
            type="checkbox"
            checked={selected}
            onChange={(event) => onSelect(event.target.checked)}
            disabled={selectionDisabled}
            aria-label={`Seleccionar bloque ${block.title ?? block.type}`}
            className="h-4 w-4 rounded border-ink/20 text-brand-700 focus:ring-brand-700"
          />
        ) : null}
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Arrastrar para reordenar bloque ${block.title ?? block.type}`}
          disabled={reorderDisabled}
          className="hidden h-9 w-9 shrink-0 cursor-grab items-center justify-center rounded-lg text-ink-faint hover:bg-surface-muted active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-40 sm:flex"
        >
          <GripVertical className="h-4.5 w-4.5" style={{ height: "1.125rem", width: "1.125rem" }} aria-hidden="true" />
        </button>
        <div className="flex items-center gap-1 sm:flex-col sm:gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 w-9 p-0"
            onClick={onMoveUp}
            disabled={position === 0 || reordering || reorderDisabled}
            aria-label={`Mover bloque ${block.title ?? block.type} arriba`}
          >
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 w-9 p-0"
            onClick={onMoveDown}
            disabled={position === total - 1 || reordering || reorderDisabled}
            aria-label={`Mover bloque ${block.title ?? block.type} abajo`}
          >
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-semibold text-ink">
            {block.title || <span className="italic text-ink-faint">Sin titulo</span>}
          </p>
          {isSystem ? (
            <StatusPill tone="brand" icon={<Cog className="h-3 w-3" aria-hidden="true" />}>
              Sistema
            </StatusPill>
          ) : (
            <StatusPill tone="neutral">Personalizado</StatusPill>
          )}
          <StatusPill tone="neutral">{HOME_BLOCK_TYPE_LABELS[block.type]}</StatusPill>
          <StatusPill tone={statusMeta.tone}>{statusMeta.label}</StatusPill>
          <span className="text-xs text-ink-faint">Posicion {position + 1} de {total}</span>
        </div>
        {(block.startsAt || block.endsAt) && (
          <p className="mt-1 text-xs text-ink-faint">
            Visible: {formatDateTime(block.startsAt)} &rarr; {formatDateTime(block.endsAt)} · Hora de Madrid
          </p>
        )}
        {block.status === "published" && block.enabled && block.effectiveStatus === "finished" && (
          <p className="mt-1 text-xs font-medium text-ink-muted">Ya no se muestra en IESAPP.</p>
        )}
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
        <div className="mr-2 flex items-center gap-2">
          <span className="text-xs text-ink-faint">Habilitado</span>
          <Switch checked={block.enabled} onCheckedChange={onToggleEnabled} disabled={busy} aria-label="Bloque habilitado" />
        </div>
        <Button variant="ghost" size="sm" onClick={onPreview} aria-label="Vista previa">
          <Eye className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit} aria-label="Editar bloque">
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </Button>
        {!isSystem && (
          <Button variant="ghost" size="sm" onClick={onCreateNotification} aria-label="Crear notificacion">
            <Bell className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
        {!isSystem && (
          <Button variant="ghost" size="sm" onClick={onDelete} aria-label="Eliminar bloque">
            <Trash2 className="h-4 w-4 text-danger-600" aria-hidden="true" />
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={onTogglePublish}
          loading={busy}
          icon={
            block.status === "published" ? (
              <Download className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Upload className="h-4 w-4" aria-hidden="true" />
            )
          }
        >
          {block.status === "published" ? "Mover a borrador" : "Publicar"}
        </Button>
      </div>
    </li>
  );
}
